import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ProductDetailService } from '@features/product-detail/product-detail.service';
import { StoreStockSidebarType } from '@features/store-stock/store-stock';
import { GeolocationPositionError } from '@features/stores-overview/model/geolocation';
import { GeoLocationService } from '@features/stores-overview/service/geolocation.service';
import { StoresService } from '@features/stores-overview/service/stores.service';
import { StoreViewModel } from '@models/api.models';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GeolocationCodeError } from '@features/stores-overview/constants/error-codes.enum';
import { debounceTime, skip } from 'rxjs/operators';
import { SortFavoriteStorePipe } from '../store-stock-pipes/sort-favorite-store.pipe';
import { FilterStoresByFlagPipe } from '../store-stock-pipes/filter-stores-by-flag.pipe';
import { StoresWithStock } from '@features/store-stock/store-stock-pipes/stores-with-stock.pipe';
import { StoreMatches } from '@features/store-stock/store-stock-pipes/store-matches.pipe';
import { StoreStock } from '@features/store-stock/store-stock-pipes/store-stock.pipe';
import { TranslateSafePipe } from '@shared/translate-safe/translate-safe.pipe';
import { FormatStringPipe } from '@shared/format-string/format-string.pipe';
import { StoreStockListItemComponent } from '../store-stock-list-item/store-stock-list-item.component';
import { CheckboxComponent } from '@shared/form-elements/checkbox/checkbox.component';
import { TooltipComponent } from '@shared/tooltip/tooltip.component';
import { SvgIconComponent } from '@shared/svg-icon/svg-icon.component';
import { AsyncPipe, NgClass, NgIf, NgTemplateOutlet } from '@angular/common';
import { InputBasicComponent } from '@shared/form-elements/input/input-basic/input-basic.component';
import { LoadingDotsComponent } from '@shared/loading-dots/loading-dots.component';

@UntilDestroy()
@Component({
    selector: 'app-store-stock-list',
    templateUrl: './store-stock-list.component.html',
    styleUrls: ['./store-stock-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        InputBasicComponent,
        NgClass,
        SvgIconComponent,
        NgIf,
        TooltipComponent,
        CheckboxComponent,
        StoreStockListItemComponent,
        AsyncPipe,
        FormatStringPipe,
        TranslateSafePipe,
        StoreStock,
        StoreMatches,
        StoresWithStock,
        FilterStoresByFlagPipe,
        SortFavoriteStorePipe,
        LoadingDotsComponent,
        NgTemplateOutlet
    ]
})
export class StoreStockListComponent implements OnInit {
    public searchInput: UntypedFormControl = new UntypedFormControl('');
    public checkbox: UntypedFormControl = new UntypedFormControl(false);
    public currentPositionError: GeolocationPositionError;
    public LocationCodeError = GeolocationCodeError;
    public activeGeo = false;

    @Input()
    type: StoreStockSidebarType;

    @Output()
    closeOverlay = new EventEmitter<void>();

    @Output()
    reserveStoreChosen = new EventEmitter<void>();

    constructor(
        public readonly storesService: StoresService,
        public readonly geoLocationService: GeoLocationService,
        public readonly productDetailService: ProductDetailService
    ) {}

    ngOnInit(): void {
        // Reset stores when initializing
        this.storesService.resetFilter();

        // Do a full search
        this.storesService.searchStoresBy({});

        this.searchInput.valueChanges.pipe(skip(1), debounceTime(200), untilDestroyed(this)).subscribe(searchValue => {
            this.activeGeo = false;
            this.storesService.searchStoresBy(searchValue ? { address: searchValue } : {});
        });

        this.geoLocationService.currentPosition$.pipe(untilDestroyed(this)).subscribe(
            ({ coords: { latitude, longitude } }) => {
                if (latitude && longitude) {
                    this.storesService.searchStoresBy({ latitude, longitude });
                    this.activeGeo = true;
                }
            },
            (error: GeolocationPositionError) => {
                this.storesService.searchStoresBy({});
                this.activeGeo = false;
                this.currentPositionError = error;
            }
        );
    }

    public disableLocationService(fetchStores?: boolean): void {
        this.activeGeo = false;
        if (fetchStores) {
            this.storesService.searchStoresBy({});
        }
    }

    public setStore(store?: StoreViewModel): void {
        if (store) {
            this.storesService.setFavoriteStore(store);
        }

        if (this.type === 'reserve') {
            this.reserveStoreChosen.emit();
        } else {
            this.closeOverlay.emit();
        }
    }

    public getGeoLocation(): void {
        this.geoLocationService.activateGeoLocation();
    }

    public removeActiveGeo(): void {
        this.activeGeo = false;
        this.storesService.searchStoresBy({});
    }

    public trackByStore(index: number, store: StoreViewModel): string | number {
        return store?.storeId || index;
    }
}
